import { useState, useRef } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import styles from './AppLayout.module.css';
import { OfflineBanner } from './OfflineBanner';
import { useSession } from '@client/lib/useSession';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /**
   * Destinations a CREW user may see. Absent means OWNER-only, which is the
   * default on purpose: an entry added here and never thought about is hidden
   * from crew rather than shown to them, matching the server's default-deny
   * allowlist in middleware/roles.ts.
   *
   * Hiding a link is a courtesy, NOT the security boundary — the server refuses
   * these routes regardless of what the client renders.
   */
  crew?: true;
}

// Only destinations that are actually built appear here. /dashboard and /jobs
// are still placeholder screens, so linking to them would send the owner to an
// empty page; they are deliberately omitted until they exist. Today is first
// because it is the screen the owner opens standing in a yard.
const primaryNav: NavItem[] = [
  { to: '/today', label: 'Today', icon: DashboardIcon(), crew: true },
  { to: '/calendar', label: 'Calendar', icon: CalendarIcon(), crew: true },
  { to: '/customers', label: 'Customers', icon: CustomersIcon() },
  { to: '/jobs/new', label: 'New job', icon: JobsIcon() },
  { to: '/invoices', label: 'Invoices', icon: InvoicesIcon() },
];

const moreNav: NavItem[] = [
  { to: '/quotes', label: 'Quotes', icon: QuotesIcon() },
  { to: '/services', label: 'Services', icon: SettingsIcon() },
  { to: '/recurring', label: 'Recurring', icon: SettingsIcon() },
  { to: '/dashboard', label: 'Reports', icon: InvoicesIcon() },
  { to: '/settings', label: 'Settings', icon: SettingsIcon() },
];

const allNav: NavItem[] = [...primaryNav, ...moreNav];

/** The nav a given role should see. */
export function navForRole(items: NavItem[], role: string | undefined): NavItem[] {
  // An unknown or missing role is treated as crew, never as owner. A session
  // that has not loaded yet must not flash the invoice total on a helper's
  // phone before the role arrives.
  return role === 'OWNER' ? items : items.filter((item) => item.crew === true);
}

function DashboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12l9-9 9 9v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <path d="M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
    </svg>
  );
}

function CustomersIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M9 21v-2a4 4 0 0 1 3-3.87" />
      <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
    </svg>
  );
}

function JobsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l4-4a1 1 0 0 0-1.4-1.4l-10 10A2 2 0 0 0 7 14v4h4a2 2 0 0 0 1.42-.58l6-6a1 1 0 0 0 0-1.4z" />
    </svg>
  );
}

function InvoicesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8z" />
      <polyline points="10 2 10 8 16 8" />
      <line x1="22" y1="8" x2="14" y2="8" />
    </svg>
  );
}

function QuotesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8" />
      <path d="M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 10v6M4.22 4.22l4.24 4.24m7.08 7.08l4.24 4.24M1 12h6m10 0h6" />
    </svg>
  );
}

function NavLinkRenderer({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => (isActive ? styles.navLinkActive : styles.navLink)}
      aria-current={undefined}
    >
      <span className={styles.navIcon}>{item.icon}</span>
      <span className={styles.navLabel}>{item.label}</span>
    </NavLink>
  );
}

/**
 * Sign-out control. Calls the session context's `logout` (which POSTs to the
 * server, then clears local state and sets status to 'anonymous'). While the
 * promise is in flight the button is disabled and carries aria-busy="true" so
 * a double-tap cannot fire two logouts. If logout rejects, the button returns
 * to its enabled state rather than staying stuck.
 */
function LogoutButton() {
  const { logout } = useSession();
  const [pending, setPending] = useState(false);
  // A ref, not the `pending` state, is what actually enforces exactly-once.
  // Two clicks dispatched in the same tick both run before React re-renders,
  // so both closures observe pending === false and both fire — and `disabled`
  // has not been applied yet either. A ref updates synchronously, so the
  // second click sees the first one's write. The state is only for rendering.
  const inFlight = useRef(false);

  const handleLogout = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    // .finally() PROPAGATES a rejection, so `logout().finally(...)` alone
    // leaves an unhandled promise rejection whenever logout throws. The catch
    // is what makes the re-enable path clean rather than merely visible.
    logout()
      .catch(() => undefined)
      .finally(() => {
        inFlight.current = false;
        setPending(false);
      });
  };

  return (
    <button
      type="button"
      className={styles.logoutButton}
      onClick={handleLogout}
      disabled={pending}
      aria-busy={pending}
    >
      <span className={styles.logoutIcon}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {/*
            A <polyline> takes a flat list of coordinates, not path syntax —
            "15 11l5 5-5 5" is a `d` attribute in a `points` slot, so the
            browser parses the first pair and discards the rest, leaving no
            arrow at all.
          */}
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      </span>
      <span className={styles.logoutLabel}>Log out</span>
    </button>
  );
}

/**
 * App-wide layout: persistent left sidebar on desktop (>= 768px),
 * fixed bottom navigation on mobile. The five most-used destinations
 * live in the bottom bar; Quotes and Settings are behind "More".
 */
export function AppLayout() {
  const { user } = useSession();
  const sidebarNav = navForRole(allNav, user?.role);
  const bottomNav = navForRole(primaryNav, user?.role);
  const overflowNav = navForRole(moreNav, user?.role);

  return (
    <div className={styles.layout}>
      <OfflineBanner />
      <a href="#main" className={styles.skipLink}>
        Skip to content
      </a>

      <nav className={styles.sidebar} aria-label="Primary">
        <ul className={styles.sidebarList}>
          {sidebarNav.map((item) => (
            <li key={item.to}>
              <NavLinkRenderer item={item} />
            </li>
          ))}
          <li>
            <LogoutButton />
          </li>
        </ul>
      </nav>

      <main id="main" className={styles.main}>
        <Outlet />
      </main>

      <nav className={styles.bottomNav} aria-label="Primary">
        <ul className={styles.bottomNavList}>
          {bottomNav.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  isActive ? styles.bottomNavLinkActive : styles.bottomNavLink
                }
                aria-current={undefined}
              >
                <span className={styles.bottomNavIcon}>{item.icon}</span>
                <span className={styles.bottomNavLabel}>{item.label}</span>
              </NavLink>
            </li>
          ))}
          <li>
            <details className={styles.moreMenu}>
              <summary className={styles.moreSummary} aria-label="More">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={styles.moreIcon}
                >
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="19" cy="12" r="1" />
                  <circle cx="5" cy="12" r="1" />
                </svg>
                <span className={styles.bottomNavLabel}>More</span>
              </summary>
              {/*
                The menu keeps its place for every role: it also holds Log out,
                which everyone needs. For a crew user `overflowNav` is empty and
                only the sign-out control renders.
              */}
              <ul className={styles.moreList}>
                {overflowNav.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        isActive ? styles.moreLinkActive : styles.moreLink
                      }
                      aria-current={undefined}
                    >
                      <span className={styles.moreLinkIcon}>{item.icon}</span>
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
                <li>
                  <LogoutButton />
                </li>
              </ul>
            </details>
          </li>
        </ul>
      </nav>
    </div>
  );
}
